import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import type { Conversation, Message } from '@/types/conversation'

const CONVERSATIONS_COLLECTION = 'conversations'

// Get conversation by ID
export async function getConversation(id: string): Promise<Conversation | null> {
  const docRef = doc(db, CONVERSATIONS_COLLECTION, id)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) return null

  return { id: docSnap.id, ...docSnap.data() } as Conversation
}

// Get all conversations for a user
export async function getUserConversations(userId: string): Promise<Conversation[]> {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('participants', 'array-contains', userId)
  )

  const snapshot = await getDocs(q)
  const conversations = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Conversation[]
  // Sort by lastMessageAt descending (client-side)
  conversations.sort((a, b) => {
    const aTime = a.lastMessageAt?.toMillis?.() || 0
    const bTime = b.lastMessageAt?.toMillis?.() || 0
    return bTime - aTime
  })
  return conversations
}

// Subscribe to user's conversations (real-time)
// Note: orderBy removed to avoid composite index requirement — sort client-side
export function subscribeToConversations(
  userId: string,
  callback: (conversations: Conversation[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('participants', 'array-contains', userId)
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const conversations = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Conversation[]
      // Sort by lastMessageAt descending (client-side)
      conversations.sort((a, b) => {
        const aTime = a.lastMessageAt?.toMillis?.() || 0
        const bTime = b.lastMessageAt?.toMillis?.() || 0
        return bTime - aTime
      })
      callback(conversations)
    },
    (error) => {
      console.error('subscribeToConversations error:', error)
      onError?.(error)
      // Still call callback with empty array so loading state resolves
      callback([])
    }
  )
}

// Find existing conversation between two users about a listing
export async function findConversation(
  userId1: string,
  userId2: string,
  listingId: string
): Promise<Conversation | null> {
  // Create sorted participants array for consistent querying
  const participants = [userId1, userId2].sort()

  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('participants', '==', participants),
    where('listingId', '==', listingId),
    limit(1)
  )

  const snapshot = await getDocs(q)
  if (snapshot.empty) return null

  const doc = snapshot.docs[0]
  return { id: doc.id, ...doc.data() } as Conversation
}

// Create a new conversation
export async function createConversation(data: {
  participants: string[]
  participantNames: Record<string, string>
  listingId: string
  listingTitle: string
  listingImage?: string
  listingPrice?: number
  initialMessage: string
  senderId: string
  senderName: string
}): Promise<string> {
  // Sort participants for consistent querying
  const sortedParticipants = [...data.participants].sort()

  const conversationData = {
    participants: sortedParticipants,
    participantNames: data.participantNames,
    listingId: data.listingId,
    listingTitle: data.listingTitle,
    listingImage: data.listingImage || null,
    listingPrice: data.listingPrice || null,
    lastMessage: data.initialMessage,
    lastMessageAt: serverTimestamp(),
    lastMessageBy: data.senderId,
    unreadBy: data.participants.find((p) => p !== data.senderId) || null,
    createdAt: serverTimestamp(),
  }

  const conversationRef = await addDoc(
    collection(db, CONVERSATIONS_COLLECTION),
    conversationData
  )

  // Add the initial message to the messages subcollection
  await addDoc(
    collection(db, CONVERSATIONS_COLLECTION, conversationRef.id, 'messages'),
    {
      senderId: data.senderId,
      senderName: data.senderName,
      text: data.initialMessage,
      read: false,
      createdAt: serverTimestamp(),
    }
  )

  return conversationRef.id
}

// Get messages for a conversation
export async function getMessages(
  conversationId: string,
  limitCount: number = 50
): Promise<Message[]> {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION, conversationId, 'messages'),
    orderBy('createdAt', 'asc'),
    limit(limitCount)
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    conversationId,
    ...doc.data(),
  })) as Message[]
}

// Subscribe to messages (real-time)
export function subscribeToMessages(
  conversationId: string,
  callback: (messages: Message[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION, conversationId, 'messages'),
    orderBy('createdAt', 'asc')
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs.map((doc) => ({
        id: doc.id,
        conversationId,
        ...doc.data(),
      })) as Message[]
      callback(messages)
    },
    (error) => {
      console.error('subscribeToMessages error:', error)
      onError?.(error)
      callback([])
    }
  )
}

// Send a message
export async function sendMessage(
  conversationId: string,
  senderId: string,
  senderName: string,
  text: string,
  imageUrl?: string
): Promise<string> {
  // Add the message
  const messageData: Record<string, unknown> = {
    senderId,
    senderName,
    text,
    read: false,
    createdAt: serverTimestamp(),
  }
  if (imageUrl) {
    messageData.imageUrl = imageUrl
  }

  const messageRef = await addDoc(
    collection(db, CONVERSATIONS_COLLECTION, conversationId, 'messages'),
    messageData
  )

  // Get conversation to find the other participant
  const conversation = await getConversation(conversationId)
  const otherParticipant = conversation?.participants.find((p) => p !== senderId)

  // Update conversation's last message
  // For image messages: show the caption in the sidebar preview if present,
  // otherwise fall back to a 📷 marker (avoids the legacy '[Image]' string).
  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId)
  await updateDoc(conversationRef, {
    lastMessage: imageUrl ? (text.trim() || '📷 Photo') : text,
    lastMessageAt: serverTimestamp(),
    lastMessageBy: senderId,
    unreadBy: otherParticipant || null,
  })

  return messageRef.id
}

// Mark conversation as read
export async function markConversationAsRead(
  conversationId: string,
  userId: string
): Promise<void> {
  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId)
  const conversation = await getConversation(conversationId)

  // Only update if this user has unread messages
  if (conversation?.unreadBy === userId) {
    await updateDoc(conversationRef, {
      unreadBy: null,
    })
  }

  // Mark all messages as read
  const messagesSnapshot = await getDocs(
    query(
      collection(db, CONVERSATIONS_COLLECTION, conversationId, 'messages'),
      where('senderId', '!=', userId),
      where('read', '==', false)
    )
  )

  const updatePromises = messagesSnapshot.docs.map((doc) =>
    updateDoc(doc.ref, { read: true })
  )

  await Promise.all(updatePromises)
}

// Get unread conversation count for a user
export async function getUnreadCount(userId: string): Promise<number> {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('participants', 'array-contains', userId),
    where('unreadBy', '==', userId)
  )

  const snapshot = await getDocs(q)
  return snapshot.size
}

// Start or continue conversation (helper function)
export async function startConversation(data: {
  senderId: string
  senderName: string
  recipientId: string
  recipientName: string
  listingId: string
  listingTitle: string
  listingImage?: string
  listingPrice?: number
  message: string
}): Promise<string> {
  // Check if conversation already exists
  const existing = await findConversation(data.senderId, data.recipientId, data.listingId)

  if (existing) {
    // Add message to existing conversation
    await sendMessage(existing.id, data.senderId, data.senderName, data.message)
    return existing.id
  }

  // Create new conversation
  return createConversation({
    participants: [data.senderId, data.recipientId],
    participantNames: {
      [data.senderId]: data.senderName,
      [data.recipientId]: data.recipientName,
    },
    listingId: data.listingId,
    listingTitle: data.listingTitle,
    listingImage: data.listingImage,
    listingPrice: data.listingPrice,
    initialMessage: data.message,
    senderId: data.senderId,
    senderName: data.senderName,
  })
}
